import { useState, useEffect, useCallback } from 'react';
import {
  BuildingOfficeIcon,
  DocumentCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from '../../i18n';
import {
  fetchHotels,
  fetchHotelKYC,
  approveHotel,
  rejectHotel,
  HotelKYCView,
  KYCDocumentView,
} from '../../services/hotelAdminService';
import { Hotel } from '../../types/hotel';

export default function HotelOnboardingPage() {
  const { t } = useTranslation();
  const [pendingHotels, setPendingHotels] = useState<Hotel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedHotel, setSelectedHotel] = useState<Hotel | null>(null);
  const [kycData, setKycData] = useState<HotelKYCView | null>(null);
  const [kycLoading, setKycLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadPendingHotels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Align with approve API: only kyc_status=pending
      const res = await fetchHotels({ limit: 100, kyc_status: 'pending' });
      const pending = (res.data || []).filter(
        (h) => ((h as { kyc_status?: string }).kyc_status || '').toLowerCase() === 'pending'
          || h.status === 'pending'
      );
      setPendingHotels(pending);
    } catch (err: any) {
      setError(err.message || t('dashboard.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadPendingHotels();
  }, [loadPendingHotels]);

  const handleViewKYC = async (hotel: Hotel) => {
    setSelectedHotel(hotel);
    setKycData(null);
    setKycLoading(true);
    try {
      const data = await fetchHotelKYC(hotel.id);
      setKycData(data);
    } catch (err: any) {
      setKycData(null);
    } finally {
      setKycLoading(false);
    }
  };

  const handleApprove = async (hotelId: string) => {
    setActionLoading(true);
    setError(null);
    try {
      await approveHotel(hotelId);
      setSuccessMessage(t('onboarding.approveSuccess'));
      setSelectedHotel(null);
      setKycData(null);
      await loadPendingHotels();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenRejectModal = () => {
    setRejectReason('');
    setRejectError('');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      setRejectError(t('onboarding.rejectionRequired'));
      return;
    }
    if (!selectedHotel) return;
    setActionLoading(true);
    setError(null);
    try {
      await rejectHotel(selectedHotel.id, rejectReason.trim());
      setShowRejectModal(false);
      setSuccessMessage(t('onboarding.rejectSuccess'));
      setSelectedHotel(null);
      setKycData(null);
      await loadPendingHotels();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getDocTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      business_license: t('onboarding.docBusinessLicense'),
      trade_register: t('onboarding.docTradeRegister'),
      tax_certificate: t('onboarding.docTaxCertificate'),
      other: t('onboarding.docOther'),
    };
    return labels[type] || type;
  };

  if (loading && pendingHotels.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-200 rounded-lg mb-4" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <BuildingOfficeIcon className="h-8 w-8 text-amber-600 mr-3" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('onboarding.title')}</h1>
            <p className="text-gray-600 mt-1">
              {pendingHotels.length} {t('onboarding.pendingHotels').toLowerCase()}
            </p>
          </div>
        </div>
        <button
          onClick={loadPendingHotels}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
          title={t('dashboard.reload')}
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Success message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
          <p className="text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-3" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button
            onClick={loadPendingHotels}
            className="px-3 py-1 text-sm border border-red-300 rounded text-red-700 hover:bg-red-100"
          >
            {t('common.retry')}
          </button>
        </div>
      )}

      {/* Main content: list + detail panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending hotels list */}
        <div className="lg:col-span-1">
          {pendingHotels.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <CheckCircleIcon className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-gray-500">{t('onboarding.noPending')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingHotels.map((hotel) => (
                <div
                  key={hotel.id}
                  className={`bg-white rounded-lg shadow p-4 cursor-pointer transition border-2 ${
                    selectedHotel?.id === hotel.id
                      ? 'border-amber-500'
                      : 'border-transparent hover:border-gray-200'
                  }`}
                  onClick={() => handleViewKYC(hotel)}
                  role="button"
                  tabIndex={0}
                  aria-label={`${t('onboarding.viewKYC')} - ${hotel.name}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleViewKYC(hotel);
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{hotel.name}</h3>
                      <p className="text-sm text-gray-500">
                        {hotel.city || hotel.province}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {hotel.email}
                      </p>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      {t('onboarding.statusPending')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail panel */}
        <div className="lg:col-span-2">
          {!selectedHotel ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <EyeIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">{t('onboarding.selectHotel')}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow">
              {/* Hotel info header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{selectedHotel.name}</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedHotel.address} — {selectedHotel.city || selectedHotel.province}
                    </p>
                    {selectedHotel.manager && (
                      <p className="text-sm text-gray-500">
                        {t('hotels.manager')}: {selectedHotel.manager}
                      </p>
                    )}
                    <p className="text-sm text-gray-500">{selectedHotel.email} · {selectedHotel.phone}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleApprove(selectedHotel.id)}
                      disabled={actionLoading}
                      className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                    >
                      <CheckCircleIcon className="w-4 h-4 mr-1.5" />
                      {t('onboarding.approve')}
                    </button>
                    <button
                      onClick={handleOpenRejectModal}
                      disabled={actionLoading}
                      className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
                    >
                      <XCircleIcon className="w-4 h-4 mr-1.5" />
                      {t('onboarding.reject')}
                    </button>
                  </div>
                </div>
              </div>

              {/* KYC Documents */}
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <DocumentCheckIcon className="w-5 h-5 mr-2 text-amber-600" />
                  {t('onboarding.viewKYC')}
                </h3>

                {kycLoading ? (
                  <div className="animate-pulse space-y-3">
                    {[1, 2].map((i) => (
                      <div key={i} className="h-16 bg-gray-100 rounded" />
                    ))}
                  </div>
                ) : kycData && kycData.documents && kycData.documents.length > 0 ? (
                  <div className="space-y-3">
                    {kycData.documents.map((doc: KYCDocumentView) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="flex items-center">
                          <DocumentCheckIcon className="w-8 h-8 text-blue-500 mr-3" />
                          <div>
                            <p className="font-medium text-gray-900">
                              {getDocTypeLabel(doc.document_type)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {doc.mime_type} · {formatFileSize(doc.file_size)} · {new Date(doc.submitted_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              doc.status === 'approved'
                                ? 'bg-green-100 text-green-800'
                                : doc.status === 'rejected'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {doc.status}
                          </span>
                          {doc.file_path && (
                            <a
                              href={doc.file_path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-2 py-1 text-xs text-blue-600 hover:text-blue-800"
                              aria-label={`Download ${getDocTypeLabel(doc.document_type)}`}
                            >
                              <DocumentArrowDownIcon className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">{t('onboarding.noDocuments')}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="reject-modal-title">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black bg-opacity-40" onClick={() => setShowRejectModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 id="reject-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
                {t('onboarding.reject')} — {selectedHotel?.name}
              </h3>
              <div className="mb-4">
                <label htmlFor="reject-reason" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('onboarding.rejectionReason')} *
                </label>
                <textarea
                  id="reject-reason"
                  rows={4}
                  value={rejectReason}
                  onChange={(e) => {
                    setRejectReason(e.target.value);
                    if (e.target.value.trim()) setRejectError('');
                  }}
                  className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-red-500 focus:border-red-500 ${
                    rejectError ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={t('onboarding.rejectionPlaceholder')}
                />
                {rejectError && (
                  <p className="text-sm text-red-600 mt-1">{rejectError}</p>
                )}
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowRejectModal(false)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleReject}
                  disabled={actionLoading}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {actionLoading ? t('common.loading') : t('common.confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
